import React, { useState } from 'react';
import {
  Button,
  Col,
  Collapse,
  Modal,
  notification,
  Row,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd';
import { CloudUploadOutlined, InboxOutlined } from '@ant-design/icons';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import useFetch from '../../../../../hooks/useFetch';
import { FetchApiInterface } from '../../../../../interfaces/FetchApi.interface';
import { ResponseInterface } from '../../../../../interfaces/Response.interface';

const { Panel } = Collapse;
const { Text } = Typography;
const { Dragger } = Upload;

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff'];
const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.tif';

interface ProcessResult {
  processed?: any[];
  notFound?: any[];
  errors?: any[];
  [key: string]: any;
}

const UploadDocuments: React.FC = () => {
  const { fetchApi, loading } = useFetch();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [result, setResult] = useState<ProcessResult | null>(null);

  const handleClose = () => {
    setOpen(false);
    setFileList([]);
    setResult(null);
  };

  const handleBeforeUpload = (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      notification.warning({ message: t('invalidFileType', { name: file.name }) });
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleUpload = () => {
    if (fileList.length === 0) {
      notification.warning({ message: t('selectAtLeastOneFile') });
      return;
    }

    const formData = new FormData();
    fileList.forEach(f => {
      formData.append('files', (f.originFileObj ?? f) as File);
    });

    const dataSend: FetchApiInterface = {
      url: '/comms/process_documents',
      data: formData,
      formData: true,
    };

    setResult(null);
    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<ResponseInterface>) => {
        const responseData: ProcessResult = data?.data ?? data;
        setResult(responseData);
        notification.success({ message: t('documentsProcessedSuccess') });
        setFileList([]);
      }
    ).catch((err: any) => {
      notification.error({ message: err?.message || t('errorProcessingDocuments') });
    });
  };

  const processed = result?.processed ?? [];
  const notFound = result?.notFound ?? [];
  const errors = result?.errors ?? [];
  const hasResults = processed.length > 0 || notFound.length > 0 || errors.length > 0;

  const renderList = (items: any[]) => (
    <ul style={{ margin: 0, paddingLeft: 20 }}>
      {items.map((item: any, i: number) => (
        <li key={i}>
          <Text>{typeof item === 'string' ? item : item?.fileName ?? JSON.stringify(item)}</Text>
          {item?.reason && <Text type='secondary'> — {item.reason}</Text>}
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <Tooltip title={t('uploadDocuments')}>
        <Button icon={<CloudUploadOutlined />} onClick={() => setOpen(true)} />
      </Tooltip>

      <Modal
        centered
        open={open}
        title={t('uploadDocuments')}
        onCancel={handleClose}
        width={560}
        footer={
          <Row justify='end' gutter={8}>
            <Col><Button onClick={handleClose}>{t('cancel')}</Button></Col>
            <Col>
              <Button
                type='primary'
                icon={<CloudUploadOutlined />}
                loading={loading}
                disabled={fileList.length === 0}
                onClick={handleUpload}
              >
                {t('process')}
              </Button>
            </Col>
          </Row>
        }
      >
        <Dragger
          multiple
          accept={ACCEPTED_EXTENSIONS}
          fileList={fileList}
          beforeUpload={handleBeforeUpload}
          onChange={({ fileList: newList }) => setFileList(newList)}
          disabled={loading}
        >
          <p className='ant-upload-drag-icon'><InboxOutlined /></p>
          <p className='ant-upload-text'>{t('dragFilesHere')}</p>
          <p className='ant-upload-hint'>
            {t('acceptedFilesHintPrefix')} <Tag color='blue'>PDF</Tag> {t('acceptedFilesHintSuffix')} <Tag color='green'>{t('images')}</Tag>
          </p>
        </Dragger>

        {result && hasResults && (
          <Collapse
            style={{ marginTop: 16 }}
            defaultActiveKey={['processed', 'notFound', 'errors']}
          >
            {processed.length > 0 && (
              <Panel key='processed' header={<Text strong style={{ color: '#52c41a' }}>{t('processed')} ({processed.length})</Text>}>
                {renderList(processed)}
              </Panel>
            )}
            {notFound.length > 0 && (
              <Panel key='notFound' header={<Text strong style={{ color: '#faad14' }}>{t('notFound')} ({notFound.length})</Text>}>
                {renderList(notFound)}
              </Panel>
            )}
            {errors.length > 0 && (
              <Panel key='errors' header={<Text strong style={{ color: '#ff4d4f' }}>{t('withErrors')} ({errors.length})</Text>}>
                {renderList(errors)}
              </Panel>
            )}
          </Collapse>
        )}

        {result && !hasResults && (
          <Text type='secondary' style={{ display: 'block', marginTop: 16, textAlign: 'center' }}>
            {t('noDetailedResults')}
          </Text>
        )}
      </Modal>
    </>
  );
};

export default UploadDocuments;
